import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Card, Table, DatePicker, Select, Input, Button, Typography, Space, Tag, Popconfirm } from 'antd';
import { DownloadOutlined, DownOutlined, ClearOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { api } from '../api';
import { t } from '../i18n';
import { HintTooltip as Tooltip } from '../components/HintTooltip';
import { fmtDate, fmtDateTime } from '../time';

function fmtTs(unix: number): string {
  return fmtDateTime(unix);
}

const ACTOR_KINDS = ['superadmin', 'admin', 'client', 'device', 'system'] as const;
const PAGE_SIZE = 50;

type AuditItem = Awaited<ReturnType<typeof api.listAudit>>['items'][number];

// Human-readable labels for the raw action codes. Unknown codes fall back to a
// prettified form (underscores → spaces).
const ACTION_LABELS: Record<string, string> = {
  login: 'Signed in',
  logout: 'Signed out',
  login_failed: 'Failed sign-in',
  login_locked_out: 'Sign-in locked out',
  password_changed: 'Changed password',
  password_change_failed: 'Failed password change',
  user_created: 'Created user',
  user_role_changed: 'Changed user role',
  user_password_reset: 'Reset user password',
  device_revoked: 'Revoked device',
  device_reactivated: 'Reactivated device',
  device_deleted: 'Deleted device',
  device_approved: 'Approved device',
  device_denied: 'Denied device',
  response_built: 'Flashed disks',
  restore_response_built: 'Restored disks from backup',
  inventory_received: 'Reported disks',
  inventory_rejected: 'Rejected inventory',
  apply_confirm: 'Confirmed flash',
  post_apply_reinventory: 'Re-read disks after flash',
  quota_granted: 'Granted quota',
  user_impersonated: 'Started impersonation',
  impersonation_ended: 'Ended impersonation',
  mtls_rejected: 'Rejected (mTLS)',
  device_operation_failed: 'Device reported a failure',
};
function actionLabel(a: string): string {
  return ACTION_LABELS[a] ?? a.replace(/_/g, ' ');
}

// Backup capture kinds → human label (mirrors the backup form).
const SOURCE_LABELS: Record<string, string> = {
  pre_apply: 'Before Flashing',
  post_apply_snapshot: 'After Flashing',
  inventory_snapshot: 'Reported',
  manual: 'Manual',
};
const day = (unix?: number | null): string => (unix ? fmtDate(unix) : '');

function changesStr(ch?: Record<string, unknown> | null): string {
  if (!ch || typeof ch !== 'object') return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(ch)) {
    if (v === null || v === undefined || v === '') continue;
    // A nested group like `elabelFields` — flatten it to its own key=value
    // pairs and drop the wrapper name, so we never print `elabelFields=[object
    // Object]`. Matches how the flash history strips the `elabelFields.` prefix.
    if (typeof v === 'object' && !Array.isArray(v)) {
      for (const [ik, iv] of Object.entries(v as Record<string, unknown>)) {
        if (iv === null || iv === undefined || iv === '') continue;
        parts.push(`${ik}=${String(iv)}`);
      }
    } else {
      parts.push(`${k}=${String(v)}`);
    }
  }
  return parts.join(' ');
}

/** Render the detail JSON as one line per disk/fact, with nice forms for the
 * disk-heavy actions (reported / restored / confirmed flash). */
function detailsLines(r: AuditItem): string[] {
  const d = r.detail as Record<string, unknown> | null;
  if (!d || typeof d !== 'object') return [];
  const lines: string[] = [];

  // Reported disks → one disk per line.
  if (Array.isArray(d.disks)) {
    for (const x of d.disks as Array<{ serial?: string; bom?: string }>) {
      if (!x?.serial) continue;
      lines.push(x.bom ? `${x.serial} — BOM ${x.bom}` : `${x.serial}`);
    }
  }
  // Restored disks → "SN ← Before Flashing @ 2026-06-15".
  if (Array.isArray(d.restored)) {
    for (const x of d.restored as Array<{ serial?: string; backupSource?: string; backupDate?: number }>) {
      const src = x.backupSource ? (SOURCE_LABELS[x.backupSource] ?? x.backupSource) : 'backup';
      const when = day(x.backupDate);
      lines.push(`${x.serial} ← ${src}${when ? ` @ ${when}` : ''}`);
    }
  }
  // Flashed disks (response_built): per-disk requested changes.
  if (d.changes && typeof d.changes === 'object' && !Array.isArray(d.changes)) {
    for (const [serial, ch] of Object.entries(d.changes as Record<string, Record<string, unknown>>)) {
      const cs = changesStr(ch);
      if (cs) lines.push(`${serial} — ${cs}`);
    }
  }
  // Confirmed flash (apply_confirm): per-disk status + what was set.
  if (Array.isArray(d.results)) {
    for (const x of d.results as Array<{ serial?: string; status?: string; changes?: Record<string, string> | null; detail?: string }>) {
      const cs = changesStr(x.changes);
      const det = x.detail ? ` (${x.detail})` : '';
      lines.push(`${x.serial} — ${x.status}${cs ? ` · ${cs}` : ''}${det}`);
    }
  }

  for (const [k, v] of Object.entries(d)) {
    if (['disks', 'restored', 'results', 'changes', 'applied', 'rejected'].includes(k)) continue;
    if (v === null || v === undefined || v === '') continue;
    let val: string;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      val = v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
    } else if (typeof v === 'object') {
      val = JSON.stringify(v);
    } else {
      val = String(v);
    }
    if (!val) continue;
    if (val.length > 120) val = `${val.slice(0, 117)}…`;
    lines.push(`${k}: ${val}`);
  }
  return lines;
}

/** Actor display: email for users, device name/id for devices, else the kind. */
function actorLabel(r: AuditItem, deviceName?: string): string {
  if (r.actorKind === 'device') {
    return deviceName || (r.actorId ? `device ${r.actorId.substring(0, 8)}` : 'device');
  }
  if (r.actorEmail) return r.actorEmail;
  if (r.actorId) return `${r.actorKind} ${r.actorId.substring(0, 8)}`;
  return r.actorKind;
}

export function AuditLog() {
  const [action, setAction] = useState('');
  const [actorKind, setActorKind] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: api.listDevices });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: api.listUsers });
  const meQ = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 });
  const isSuper = meQ.data?.user.role === 'superadmin';
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();
  const clear = useMutation({
    mutationFn: () => api.clearAuditLog(),
    onSuccess: (r) => {
      message.success(t('auditClearedOk', { removed: r.removed }));
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  const actorId = deviceId || userId;
  const effectiveKind = deviceId ? 'device' : actorKind;
  const fromTs = range?.[0] ? range[0].startOf('day').unix() : undefined;
  const toTs = range?.[1] ? range[1].endOf('day').unix() : undefined;

  const q = useInfiniteQuery({
    queryKey: ['audit', action, effectiveKind, actorId, search, fromTs ?? '', toTs ?? ''],
    queryFn: ({ pageParam }) =>
      api.listAudit({
        action: action || undefined,
        actorKind: effectiveKind || undefined,
        actorId: actorId || undefined,
        q: search || undefined,
        fromTs,
        toTs,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    refetchInterval: 10_000,
  });

  const items = (q.data?.pages ?? []).flatMap((p) => p.items);
  const total = q.data?.pages[0]?.total ?? 0;

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && q.hasNextPage && !q.isFetchingNextPage) {
        void q.fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage, items.length]);

  const csvHref = ((): string => {
    const params = new URLSearchParams();
    if (action) params.set('action', action);
    if (effectiveKind) params.set('actorKind', effectiveKind);
    if (actorId) params.set('actorId', actorId);
    if (search) params.set('q', search);
    if (fromTs !== undefined) params.set('fromTs', String(fromTs));
    if (toTs !== undefined) params.set('toTs', String(toTs));
    const qs = params.toString();
    return `/api/admin/v1/audit/export.csv${qs ? `?${qs}` : ''}`;
  })();

  const deviceNameById = new Map((devicesQ.data ?? []).map((d) => [d.id, d.label || '']));

  const columns: ColumnsType<AuditItem> = [
    { title: t('auditTime'), dataIndex: 'ts', width: 160, render: (ts: number) => <span style={{ color: '#888' }}>{fmtTs(ts)}</span> },
    {
      title: t('auditUserDevice'),
      key: 'actor',
      width: 220,
      render: (_: unknown, r) => (
        <Space size={4}>
          <Typography.Text>{actorLabel(r, r.actorId ? deviceNameById.get(r.actorId) || undefined : undefined)}</Typography.Text>
          <Tag style={{ marginInlineEnd: 0 }}>{r.actorKind}</Tag>
        </Space>
      ),
    },
    {
      title: t('auditIp'),
      dataIndex: 'ip',
      width: 130,
      render: (ip: string | null) => (ip ? <Typography.Text code style={{ fontSize: 12 }}>{ip}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: t('auditAction'),
      dataIndex: 'action',
      width: 200,
      render: (a: string) => (
        <Tooltip title={a}>
          <Typography.Text>{actionLabel(a)}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: t('auditDetails'),
      key: 'details',
      render: (_: unknown, r) => {
        const lines = detailsLines(r);
        if (!lines.length) return <Typography.Text type="secondary">—</Typography.Text>;
        return (
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, marginBottom: 0, whiteSpace: 'pre-line' }}
            ellipsis={{ rows: 3, expandable: true, symbol: t('more') }}
          >
            {lines.join('\n')}
          </Typography.Paragraph>
        );
      },
    },
  ];

  return (
    <Card
      title={t('auditTitle')}
      extra={
        <Space wrap>
          <Button href={csvHref} download icon={<DownloadOutlined />}>
            {t('auditExportCsv')}
          </Button>
          {isSuper && (
            <Popconfirm title={t('auditClearConfirm')} okButtonProps={{ danger: true }} onConfirm={() => clear.mutate()}>
              <Button danger icon={<ClearOutlined />} loading={clear.isPending}>
                {t('auditClearBtn')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <DatePicker.RangePicker
          value={range as [Dayjs, Dayjs] | null}
          onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
          allowEmpty={[true, true]}
        />
        <Select
          style={{ minWidth: 170 }}
          value={deviceId || undefined}
          placeholder={t('logsAnyDevice')}
          allowClear
          onChange={(v) => {
            setDeviceId(v ?? '');
            if (v) {
              setUserId('');
              setActorKind('');
            }
          }}
          options={(devicesQ.data ?? []).map((d) => ({ value: d.id, label: `${d.id.substring(0, 8)} (${d.status})` }))}
        />
        <Select
          style={{ minWidth: 170 }}
          value={userId || undefined}
          placeholder={t('logsAnyUser')}
          allowClear
          onChange={(v) => {
            setUserId(v ?? '');
            if (v) setDeviceId('');
          }}
          options={(usersQ.data ?? []).map((u) => ({ value: u.id, label: u.username }))}
        />
        <Select
          style={{ minWidth: 130 }}
          value={actorKind || undefined}
          placeholder={t('logsAllActors')}
          allowClear
          disabled={!!deviceId}
          onChange={(v) => setActorKind(v ?? '')}
          options={ACTOR_KINDS.map((k) => ({ value: k, label: k }))}
        />
        <Input placeholder={t('logsActionPlaceholder')} value={action} onChange={(e) => setAction(e.target.value)} style={{ width: 150 }} allowClear />
        <Input placeholder={t('logsSearchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} allowClear />
        <Typography.Text type="secondary">
          {items.length} / {total}
        </Typography.Text>
      </Space>

      <Table<AuditItem>
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={q.isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowClassName={(r) => `audit-row-${r.actorKind}`}
      />
      <div ref={sentinelRef} style={{ height: 1 }} />
      <div style={{ marginTop: 12, textAlign: 'center' }}>
        {q.hasNextPage ? (
          <Button icon={<DownOutlined />} loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('logsLoadMore')}
          </Button>
        ) : (
          items.length > 0 && <Typography.Text type="secondary">{t('logsAllLoaded')}</Typography.Text>
        )}
      </div>
    </Card>
  );
}
